import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft,
  Car,
  Package,
  MapPin,
  Phone,
  User,
  Clock,
  Check,
  MapTrifold,
  Coffee,
  Lightning,
  Timer,
  CheckCircle,
  QrCode,
  Camera,
  X,
  NavigationArrow,
  Path,
  Money,
  CreditCard
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════
// DRIVER APP - Delivery Management for Drivers with QR Scanner & GPS
// ═══════════════════════════════════════════════════════════════════════════

const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  orange: "#E07A3D",
  tan: "#D4A574",
  blue: "#3B82F6",
  red: "#DC3545",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
};

// ═══════════════════════════════════════════════════════════════════════════
// QR CODE SCANNER MODAL
// ═══════════════════════════════════════════════════════════════════════════
const QRScannerModal = ({ isOpen, onClose, onScan, token }) => {
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    }
    return () => stopCamera();
  }, [isOpen]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);
      setError("");
    } catch (err) {
      console.error("Camera error:", err);
      setError("Kamera nicht verfügbar. Bitte Code manuell eingeben.");
      setScanning(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const handleManualSubmit = async () => {
    if (!manualCode.trim()) {
      setError("Bitte Code eingeben");
      return;
    }
    
    try {
      setError("");
      const res = await axios.post(
        `${API}/api/driver/scan/${encodeURIComponent(manualCode.trim())}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      onScan(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Scannen");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ backgroundColor: COLORS.white }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-4 flex items-center justify-between"
          style={{ backgroundColor: COLORS.green }}
        >
          <div className="flex items-center gap-3 text-white">
            <QrCode size={24} weight="fill" />
            <span className="font-bold">QR-Code scannen</span>
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          >
            <X size={18} className="text-white" />
          </motion.button>
        </div>

        {/* Camera View */}
        <div className="relative aspect-square bg-black">
          {scanning ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              {/* Scanner Frame */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="w-48 h-48 border-4 rounded-xl"
                  style={{ borderColor: COLORS.green }}
                >
                  {/* Corner Accents */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4" style={{ borderColor: COLORS.orange }} />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4" style={{ borderColor: COLORS.orange }} />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4" style={{ borderColor: COLORS.orange }} />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4" style={{ borderColor: COLORS.orange }} />
                </div>
                {/* Scanning Line Animation */}
                <motion.div
                  className="absolute left-1/2 transform -translate-x-1/2 w-40 h-0.5"
                  style={{ backgroundColor: COLORS.orange }}
                  animate={{ top: ["35%", "65%", "35%"] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
              <Camera size={64} weight="light" style={{ color: COLORS.textMuted }} />
              <p className="mt-4 text-sm" style={{ color: COLORS.textMuted }}>
                {error || "Kamera wird geladen..."}
              </p>
            </div>
          )}
        </div>

        {/* Manual Code Entry */}
        <div className="p-4 space-y-4">
          <p className="text-center text-sm" style={{ color: COLORS.textMuted }}>
            Oder Code manuell eingeben:
          </p>
          
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="DEL-1234-ABCD1234"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              className="flex-1 px-4 py-3 rounded-xl font-mono text-center"
              style={{ 
                backgroundColor: COLORS.bg, 
                color: COLORS.text,
                border: `2px solid ${error ? COLORS.red : COLORS.bg}`
              }}
              data-testid="manual-code-input"
            />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleManualSubmit}
              className="px-6 py-3 rounded-xl font-bold text-white"
              style={{ backgroundColor: COLORS.green }}
              data-testid="submit-code-btn"
            >
              <Check size={22} weight="bold" />
            </motion.button>
          </div>

          {error && (
            <p className="text-center text-sm font-medium" style={{ color: COLORS.red }}>
              {error}
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVE DELIVERY CARD WITH GPS & ROUTE INFO
// ═══════════════════════════════════════════════════════════════════════════
const ActiveDeliveryCard = ({ order, onStartDelivery, onDelivered, driverLocation }) => {
  const isPickedUp = order.status === "picked_up";
  const isDelivering = order.status === "delivering";

  const openNavigation = () => {
    const address = encodeURIComponent(order.delivery_address);
    
    // Try to use current location if available
    if (driverLocation) {
      const url = `https://www.google.com/maps/dir/${driverLocation.lat},${driverLocation.lng}/${address}`;
      window.open(url, "_blank");
    } else {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${address}`;
      window.open(url, "_blank");
    }
  };

  const callCustomer = () => {
    if (order.user_phone) {
      window.open(`tel:${order.user_phone}`, "_self");
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-2xl overflow-hidden"
      style={{ 
        backgroundColor: COLORS.white,
        border: `3px solid ${COLORS.green}`
      }}
      data-testid="active-delivery-card"
    >
      {/* Header */}
      <div 
        className="p-4"
        style={{ backgroundColor: COLORS.green }}
      >
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {isDelivering ? (
                <Car size={28} weight="fill" />
              ) : (
                <Package size={28} weight="fill" />
              )}
            </motion.div>
            <div>
              <p className="font-black text-xl">#{order.order_number}</p>
              <p className="text-sm opacity-80">
                {isPickedUp ? "Abholung in der Filiale" : "Unterwegs zum Kunden"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black">{order.total?.toFixed(2)}€</p>
          </div>
        </div>
      </div>

      {/* BARZAHLUNG WARNUNG - GROSS UND DEUTLICH */}
      {order.payment_method === "cash" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4"
          style={{ backgroundColor: COLORS.red }}
        >
          <div className="flex items-center gap-4 text-white">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              <Money size={36} weight="fill" />
            </motion.div>
            <div>
              <p className="font-black text-lg">💵 BARZAHLUNG</p>
              <p className="text-xl font-black">{order.total?.toFixed(2)}€ kassieren!</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Karte bei Lieferung */}
      {order.payment_method === "card" && (
        <div 
          className="p-3"
          style={{ backgroundColor: COLORS.orange + "20" }}
        >
          <div className="flex items-center gap-3">
            <CreditCard size={24} weight="fill" style={{ color: COLORS.orange }} />
            <p className="font-bold" style={{ color: COLORS.orange }}>
              💳 Kartenzahlung bei Lieferung
            </p>
          </div>
        </div>
      )}

      {/* Online bezahlt */}
      {order.payment_method === "online" && (
        <div 
          className="p-3"
          style={{ backgroundColor: COLORS.green + "15" }}
        >
          <div className="flex items-center gap-3">
            <Check size={24} weight="bold" style={{ color: COLORS.green }} />
            <p className="font-bold" style={{ color: COLORS.green }}>
              ✓ Bereits online bezahlt
            </p>
          </div>
        </div>
      )}

      {/* Status Steps */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div 
            className="flex-1 h-2 rounded-full"
            style={{ backgroundColor: COLORS.green }}
          />
          <div 
            className="flex-1 h-2 rounded-full"
            style={{ backgroundColor: isDelivering ? COLORS.green : COLORS.bg }}
          />
          <div 
            className="flex-1 h-2 rounded-full"
            style={{ backgroundColor: COLORS.bg }}
          />
        </div>

        <div className="flex justify-between text-xs mb-4" style={{ color: COLORS.textMuted }}>
          <span className="font-bold" style={{ color: COLORS.green }}>Angenommen</span>
          <span className={isDelivering ? "font-bold" : ""} style={{ color: isDelivering ? COLORS.green : undefined }}>Unterwegs</span>
          <span>Geliefert</span>
        </div>
      </div>

      {/* GPS Status */}
      {driverLocation && (
        <div className="px-4 pb-2">
          <div 
            className="flex items-center gap-2 p-2 rounded-lg text-xs"
            style={{ backgroundColor: COLORS.green + "15" }}
          >
            <NavigationArrow size={14} weight="fill" style={{ color: COLORS.green }} />
            <span style={{ color: COLORS.green }}>GPS aktiv</span>
            <span style={{ color: COLORS.textMuted }}>
              • {new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>
      )}

      {/* Items Summary */}
      <div className="px-4 pb-4">
        <div 
          className="p-3 rounded-xl"
          style={{ backgroundColor: COLORS.bg }}
        >
          <p className="text-sm font-bold mb-2" style={{ color: COLORS.text }}>
            Bestellung ({order.items?.length || 0} Artikel)
          </p>
          {order.items?.map((item, i) => (
            <p key={i} className="text-sm" style={{ color: COLORS.textMuted }}>
              {item.quantity}x {item.product_name}
            </p>
          ))}
        </div>
      </div>

      {/* Delivery Address */}
      <div className="px-4 pb-4">
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: COLORS.orange + "15" }}
        >
          <div className="flex items-start gap-3">
            <MapPin size={20} weight="fill" style={{ color: COLORS.orange }} />
            <div className="flex-1">
              <p className="font-bold" style={{ color: COLORS.text }}>
                {order.user_name || order.customer_name}
              </p>
              {/* TELEFONNUMMER - IMMER ANZEIGEN */}
              {(order.user_phone || order.customer_phone) && (
                <div className="flex items-center gap-2 mt-1">
                  <Phone size={16} weight="fill" style={{ color: COLORS.green }} />
                  <a 
                    href={`tel:${order.user_phone || order.customer_phone}`}
                    className="text-sm font-bold underline"
                    style={{ color: COLORS.green }}
                  >
                    {order.user_phone || order.customer_phone}
                  </a>
                </div>
              )}
              <p className="text-sm mt-2" style={{ color: COLORS.textMuted }}>
                {order.delivery_address}
              </p>
              {order.delivery_notes && (
                <p className="text-xs mt-2 italic" style={{ color: COLORS.textMuted }}>
                  "{order.delivery_notes}"
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 pt-0 space-y-3">
        {/* Navigation & Call */}
        <div className="flex gap-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={openNavigation}
            className="flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.blue, color: COLORS.white }}
            data-testid="navigation-btn"
          >
            <MapTrifold size={20} weight="fill" />
            Navigation
          </motion.button>
          {(order.user_phone || order.customer_phone) && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={callCustomer}
              className="w-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.green + "20" }}
              data-testid="call-customer-btn"
            >
              <Phone size={22} weight="fill" style={{ color: COLORS.green }} />
            </motion.button>
          )}
        </div>

        {/* Status Button */}
        {isPickedUp && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => onStartDelivery(order.id)}
            className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.orange }}
            data-testid="start-delivery-btn"
          >
            <Car size={22} weight="fill" />
            Abgeholt - Jetzt losfahren
          </motion.button>
        )}

        {isDelivering && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => onDelivered(order.id)}
            className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.green }}
            data-testid="delivered-btn"
          >
            <CheckCircle size={22} weight="fill" />
            Geliefert - Abschließen
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DRIVER PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function DriverPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [availableOrders, setAvailableOrders] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [stats, setStats] = useState({ deliveries_today: 0, total_deliveries: 0 });
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);

  const location = user?.location || "aachen";

  // GPS Location Tracking
  useEffect(() => {
    let watchId = null;

    const startTracking = () => {
      if (!navigator.geolocation) {
        console.log("Geolocation not supported");
        return;
      }

      watchId = navigator.geolocation.watchPosition(
        async (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setDriverLocation(newLocation);

          // Send location to backend if we have an active delivery
          if (token && activeDelivery) {
            try {
              await axios.post(
                `${API}/api/driver/location`,
                null,
                { 
                  params: { lat: newLocation.lat, lng: newLocation.lng },
                  headers: { Authorization: `Bearer ${token}` } 
                }
              );
            } catch (err) {
              console.error("Failed to update location:", err);
            }
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10000,
          timeout: 10000
        }
      );
    };

    startTracking();

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [token, activeDelivery]);

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const [availableRes, activeRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/driver/available-orders?location=${location}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/driver/active-delivery`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/driver/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAvailableOrders(availableRes.data || []);
      setActiveDelivery(activeRes.data);
      setStats(statsRes.data || { deliveries_today: 0, total_deliveries: 0 });
    } catch (err) {
      console.error("Load error:", err);
    }
    setLoading(false);
  }, [token, location]);

  useEffect(() => {
    loadData();
    // Poll every 15 seconds
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Check access
  if (!user || !["staff", "master-admin", "driver"].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: COLORS.bg }}>
        <div className="text-center">
          <Car size={64} weight="light" style={{ color: COLORS.textMuted }} className="mx-auto mb-4" />
          <h2 className="text-xl font-bold" style={{ color: COLORS.text }}>Fahrer-Zugang erforderlich</h2>
          <p className="text-sm mt-2" style={{ color: COLORS.textMuted }}>
            Bitte melde dich mit einem Fahrer-Account an.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/landing")}
            className="mt-4 px-6 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
            data-testid="login-redirect-btn"
          >
            Zur Anmeldung
          </motion.button>
        </div>
      </div>
    );
  }

  const handleScanResult = (result) => {
    // Reload data after successful scan
    loadData();
  };

  const handleStartDelivery = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/driver/orders/${orderId}/start-delivery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Auf dem Weg zum Kunden!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDelivered = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/driver/orders/${orderId}/delivered`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bestellung geliefert! Gut gemacht!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="min-h-screen pb-6" style={{ backgroundColor: COLORS.bg }} data-testid="driver-page">
      {/* Header */}
      <header className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate("/landing")}
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.white }}
              data-testid="back-btn"
            >
              <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
            </motion.button>
            <div>
              <h1 
                className="text-2xl font-black"
                style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Fahrer
              </h1>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Hallo, {user.name}!
              </p>
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={logout}
            className="px-4 py-2 rounded-xl text-sm font-bold"
            style={{ backgroundColor: COLORS.white, color: COLORS.textMuted }}
            data-testid="logout-btn"
          >
            Abmelden
          </motion.button>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div 
            className="p-4 rounded-2xl text-center"
            style={{ backgroundColor: COLORS.white }}
          >
            <p className="text-3xl font-black" style={{ color: COLORS.green }}>
              {stats.deliveries_today}
            </p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>Heute geliefert</p>
          </div>
          <div 
            className="p-4 rounded-2xl text-center"
            style={{ backgroundColor: COLORS.white }}
          >
            <p className="text-3xl font-black" style={{ color: COLORS.orange }}>
              {stats.total_deliveries}
            </p>
            <p className="text-sm" style={{ color: COLORS.textMuted }}>Gesamt</p>
          </div>
        </div>
      </div>

      {/* GPS Status */}
      <div className="px-4 mb-4">
        <div 
          className="p-3 rounded-xl flex items-center justify-between"
          style={{ backgroundColor: driverLocation ? COLORS.green + "15" : COLORS.orange + "15" }}
        >
          <div className="flex items-center gap-2">
            <NavigationArrow 
              size={18} 
              weight="fill" 
              style={{ color: driverLocation ? COLORS.green : COLORS.orange }} 
            />
            <span className="text-sm font-medium" style={{ color: driverLocation ? COLORS.green : COLORS.orange }}>
              {driverLocation ? "GPS aktiv" : "GPS nicht verfügbar"}
            </span>
          </div>
          {driverLocation && (
            <span className="text-xs" style={{ color: COLORS.textMuted }}>
              Standort wird übertragen
            </span>
          )}
        </div>
      </div>

      {/* Active Delivery (if any) */}
      {activeDelivery && (
        <div className="px-4 mb-4">
          <h2 className="text-lg font-black mb-3" style={{ color: COLORS.text }}>
            Aktive Lieferung
          </h2>
          <ActiveDeliveryCard
            order={activeDelivery}
            onStartDelivery={handleStartDelivery}
            onDelivered={handleDelivered}
            driverLocation={driverLocation}
          />
        </div>
      )}

      {/* Scan QR Code Button (only when no active delivery) */}
      {!activeDelivery && (
        <div className="px-4 mb-4">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => setShowScanner(true)}
            className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-3"
            style={{ 
              backgroundColor: COLORS.green,
              boxShadow: `0 6px 20px ${COLORS.green}40`
            }}
            data-testid="scan-qr-btn"
          >
            <QrCode size={24} weight="fill" />
            QR-Code scannen
          </motion.button>
          <p className="text-center text-xs mt-2" style={{ color: COLORS.textMuted }}>
            Scanne den QR-Code auf dem Kundenbon
          </p>
        </div>
      )}

      {/* Available Orders Info (when no active delivery) */}
      {!activeDelivery && (
        <div className="px-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-black" style={{ color: COLORS.text }}>
              Verfügbare Aufträge
            </h2>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={loadData}
              className="px-3 py-1.5 rounded-lg text-sm font-bold"
              style={{ backgroundColor: COLORS.white, color: COLORS.textMuted }}
              data-testid="refresh-btn"
            >
              Aktualisieren
            </motion.button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Car size={48} weight="light" style={{ color: COLORS.textMuted }} />
              </motion.div>
              <p className="mt-4" style={{ color: COLORS.textMuted }}>Lade Aufträge...</p>
            </div>
          ) : availableOrders.length === 0 ? (
            <div className="text-center py-12">
              <Coffee size={48} weight="light" style={{ color: COLORS.textMuted }} className="mx-auto" />
              <p className="mt-4 font-bold" style={{ color: COLORS.textMuted }}>
                Keine Aufträge verfügbar
              </p>
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Scanne einen QR-Code vom Kundenbon
              </p>
            </div>
          ) : (
            <div 
              className="p-4 rounded-2xl text-center"
              style={{ backgroundColor: COLORS.white }}
            >
              <Package size={32} weight="fill" style={{ color: COLORS.green }} className="mx-auto mb-2" />
              <p className="text-lg font-black" style={{ color: COLORS.green }}>
                {availableOrders.length} Aufträge bereit
              </p>
              <p className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                Scanne den QR-Code eines Bons, um einen Auftrag zu übernehmen
              </p>
            </div>
          )}
        </div>
      )}

      {/* QR Scanner Modal */}
      <AnimatePresence>
        {showScanner && (
          <QRScannerModal
            isOpen={showScanner}
            onClose={() => setShowScanner(false)}
            onScan={handleScanResult}
            token={token}
          />
        )}
      </AnimatePresence>

      {/* New Order Alert */}
      {!activeDelivery && availableOrders.length > 0 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
          style={{ backgroundColor: COLORS.green }}
          onClick={() => setShowScanner(true)}
        >
          <Package size={24} weight="fill" className="text-white" />
          <span 
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center"
            style={{ backgroundColor: COLORS.orange, color: COLORS.white }}
          >
            {availableOrders.length}
          </span>
        </motion.div>
      )}
    </div>
  );
}
